import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './SignalTranslation.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { initialSignalTranslationState } from '../../reducers/initialState';
import { Navigate, Link, useParams } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ContentTemplate from '../ContentTemplate/ContentTemplate';
import Banner from '../Banner/Banner';
import 'react-datetime/css/react-datetime.css';
import moment from 'moment-timezone';
import SignalTranslationResults from './SignalTranslationResults';
import SignalTranslationConstants from './SignalTranslationConstants';
import SignalTranslationComments from './SignalTranslationComments';
import SignalTranslationFinalize from './SignalTranslationFinalize';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import Cookies from 'js-cookie';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const SignalTranslationView = ({
  signalTranslationManager = {...initialSignalTranslationState.signalTranslation},
  userProfileWidgets,
  currentTimezone,
  setSignalTranslationChange,
  setSignalTranslationConstant,
  saveSignalTranslation,
  deleteSignalTranslation,
  updateUnitShadow,
  describeSignalTranslation,
  saveSignalTranslationComment,
  selectedUnit,
  intl
}) => {
  let IsEdit = false;
  let IsRedirect = false;
  let { unitSerialNumber, unitId, signalTranslationId } = useParams();
  const [active, setActive] = useState(false);
  const [commandId, setCommandId] = useState(0);
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  let userName = Cookies.get(`name-${envName}`) || '';

  const isCommentAllowed = IsWidgetAccessible(userProfileWidgets, 'SIGNALTRANSLATION_COMMENT_ADD');

  if (signalTranslationId && parseInt(signalTranslationId) > 0) {
    IsEdit = true;
    userName = signalTranslationManager.SelectedSignalTranslation.FullName;
  }

  useEffect(() => {
    if (unitSerialNumber.length > 0 && signalTranslationId > 0) {
      describeSignalTranslation(signalTranslationId, unitSerialNumber);
    } else {
      setSignalTranslationChange({
        SelectedSignalTranslation: { ...signalTranslationManager.SelectedSignalTranslation, ...initialSignalTranslationState.signalTranslation.SelectedSignalTranslation }
      });
    }
  }, [describeSignalTranslation, signalTranslationId, unitSerialNumber]);

  const returnUrl = '/unit/signal-translations/' + unitSerialNumber;
  let messageId = (signalTranslationManager && signalTranslationManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });

  const handleConfirm = () => {
    deleteSignalTranslations();
    handleClose();
  };

  const deleteSignalTranslations = () => {
    if (signalTranslationId && unitSerialNumber) {
      deleteSignalTranslation(signalTranslationId, unitSerialNumber, true);
      IsRedirect = true;
    }
  };

  const handleClose = () => setActive(false);

  const handleCommand = commandId => {
    setActive(true);
    setCommandId(commandId);
  };

  const dialogTitle = intl.formatMessage({
    id: 'signalTranslation.delete.confirmation',
    defaultMessage: 'Are you sure you want to delete this signalTranslation?'
  });

  return (
    <div className={s.signalTranslation}>
      <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />
      {IsRedirect ? <Navigate to={returnUrl} /> : ''}
      {signalTranslationManager.isOpSuccessful && !signalTranslationManager.showBanner ? <Navigate to={returnUrl} /> : ''}
      {signalTranslationManager.showBanner && (
        <Banner
          key={'b1'}
          failureText={messageText}
          showBanner={signalTranslationManager.showBanner}
          status={signalTranslationManager.isOpSuccessful}
          successText={messageText}
        />
      )}
      {signalTranslationManager.isLoading && <LoadingSpinner />}
      <ContentTemplate
        selectedPage="signalTranslation"
        userProfileWidgets={userProfileWidgets}
        widgetCode={'SIGNALTRANSLATION_VIEW'}
        tagoutRequired={false}
      >
        <div className={s.contentWrapper}>
          <Row>
            <Col sm={6} lg={8}>
              <div className={s.signalTranslationHeader}>
                <Link className={s.backLink} to={returnUrl}>
                  &lt; &nbsp;
                  <FormattedMessage id="signalTranslation.backToSignalTranslations" defaultMessage="BACK" />
                </Link>
                <h3 data-unittest="headingLabel">
                  <FormattedMessage id="signalTranslation.signalTranslation" defaultMessage="SignalTranslation" />
                </h3>
              </div>
            </Col>
            <Col sm={6} lg={4}>
              <Link to={`/unit/signal-translations/${unitSerialNumber}/edit/${unitId}/${signalTranslationId}`}>
                <Button className={s.changeSignalTranslation} style={WidgetVisibility(userProfileWidgets, `SIGNALTRANSLATION_EDIT`)}>
                  <FormattedMessage id="common.ledit" defaultMessage="Edit" />
                </Button>
              </Link>
            </Col>
          </Row>

          <div className={s.signalTranslationContent}>
            <Row className={s.breakrow}>
              <Col sm={12}>
                <h4>
                  <FormattedMessage id="signalTranslation.details" defaultMessage="Details" />
                </h4>
              </Col>
            </Row>
            <Row className={s.breakrow}>
              <Col sm={2}>
                <Form.Label>
                  <FormattedMessage id="signalTranslation.unit" defaultMessage="Unit" />
                </Form.Label>
              </Col>
              <Col sm={10}>{unitSerialNumber}</Col>
            </Row>
            <Row className={s.breakrow}>
              <Col sm={2}>
                <Form.Label>
                  <FormattedMessage id="signalTranslation.reporter" defaultMessage="Reporter" />
                </Form.Label>
              </Col>
              <Col sm={10}>{userName}</Col>
            </Row>

            <hr className={s.breakhr} />
            <Row className={s.breakrow}>
              <Col sm={2}>
                <Form.Label>
                  <FormattedMessage id="signalTranslation.sensor" defaultMessage="Sensor" />
                </Form.Label>
              </Col>
              <Col sm={10}>{signalTranslationManager.SelectedSignalTranslation.ComponentPartName}</Col>
            </Row>
            <Row className={s.breakrow}>
              <Col sm={2}>
                <Form.Label>
                  <FormattedMessage id="signalTranslation.signalTranslationComponent" defaultMessage="SignalTranslation Component" />
                </Form.Label>
              </Col>
              <Col sm={10}>
                {signalTranslationManager.SelectedSignalTranslation.ComponentPartName +
                  '_' +
                  signalTranslationManager.SelectedSignalTranslation.SignalTranslationComponentName}
              </Col>
            </Row>
            <Row className={s.breakrow}>
              <Col sm={2}>
                <Form.Label>
                  <FormattedMessage id="signalTranslation.signalTranslationDatetime" defaultMessage="SignalTranslation Date Time" />
                </Form.Label>
              </Col>
              <Col sm={10}>
                {signalTranslationManager.SelectedSignalTranslation.SignalTranslationDateTime
                  ? moment
                      .unix(signalTranslationManager.SelectedSignalTranslation.SignalTranslationDateTime)
                      .tz(currentTimezone)
                      .format('DD-MMM-YYYY HH:mm')
                  : '-'}
              </Col>
            </Row>
            <Row className={s.breakrow}>
              <Col sm={2}>
                <Form.Label>
                  <FormattedMessage id="signalTranslation.tolerance" defaultMessage="Tolerance" />
                </Form.Label>
              </Col>
              <Col sm={10}>{signalTranslationManager.SelectedSignalTranslation.SignalTranslationAcceptedErrorTolerance}</Col>
            </Row>
            <Row className={s.breakrow}>
              <Col sm={2}>
                <Form.Label>
                  <FormattedMessage id="signalTranslation.sensorRange" defaultMessage="Sensor Range" />
                </Form.Label>
              </Col>
              <Col sm={10}>
                <Row>
                  <Col md={2}>
                    <Form.Label>
                      <FormattedMessage id="signalTranslation.low" defaultMessage="Low" />
                    </Form.Label>{' '}
                    : {signalTranslationManager.SelectedSignalTranslation.OutputRangeLow}
                  </Col>
                  <Col md={2}>
                    <Form.Label>
                      <FormattedMessage id="signalTranslation.high" defaultMessage="High" />
                    </Form.Label>{' '}
                    : {signalTranslationManager.SelectedSignalTranslation.OutputRangeHigh}
                  </Col>
                  <Col md={2}>
                    <Form.Label>
                      {' '}
                      <FormattedMessage id="signalTranslation.unitOfMeasure" defaultMessage="Unit Of Measure" />
                    </Form.Label>{' '}
                    : {signalTranslationManager.SelectedSignalTranslation.UnitOfMeasure}
                  </Col>
                  <Col md={6}></Col>
                </Row>
              </Col>
            </Row>
          </div>
        </div>
        <SignalTranslationResults
          UpdateAllowed={false}
          signalTranslationManager={signalTranslationManager}
          setSignalTranslationResults={null}
          isViewMode={true}
          intl={intl}
        />
        <SignalTranslationConstants
          UpdateAllowed={false}
          signalTranslationManager={signalTranslationManager}
          setSignalTranslationRequired={null}
          setSignalTranslationConstant={setSignalTranslationConstant}
          isViewMode={true}
          intl={intl}
        />
        {isCommentAllowed && (
          <SignalTranslationComments
            UpdateAllowed={true}
            signalTranslationManager={signalTranslationManager}
            intl={intl}
            currentTimezone={currentTimezone}
            saveSignalTranslationComment={saveSignalTranslationComment}
            unitSerialNumber={unitSerialNumber}
          />
        )}
        <Row className={s.breakRow}>
          <Col sm={12}>
            <Link to={`/unit/signal-translations/${unitSerialNumber}/edit/${unitId}/${signalTranslationId}`}>
              <Button className={s.changeSignalTranslationAlt} style={WidgetVisibility(userProfileWidgets, `SIGNALTRANSLATION_COMMENT_ADD`)}>
                <FormattedMessage id="common.ledit" defaultMessage="Edit" />
              </Button>
            </Link>
            {signalTranslationId && (
              <Button
                className={s.deleteButton}
                variant="outline-secondary"
                onClick={handleCommand}
                noValidate
                data-unittest="deleteData"
                style={WidgetVisibility(userProfileWidgets, `SIGNALTRANSLATIONDELETE`)}
              >
                <FormattedMessage id="signalTranslation.delete" defaultMessage="DELETE SIGNALTRANSLATIONS" />
              </Button>
            )}
          </Col>
          <Col sm={12} md={2}></Col>
        </Row>
        <SignalTranslationFinalize
          signalTranslationManager={signalTranslationManager}
          saveSignalTranslation={saveSignalTranslation}
          unitSerialNumber={unitSerialNumber}
          updateUnitShadow={updateUnitShadow}
          userProfileWidgets={userProfileWidgets}
          selectedUnit={selectedUnit}
        />
      </ContentTemplate>
    </div>
  );
};


SignalTranslationView.propTypes = {
  describeSignalTranslation: PropTypes.func.isRequired
};

export default injectIntl(SignalTranslationView);
